import { useState } from 'react';
import type { ActivityRegisterWhileDownloadFile, DataFileTable, FileStats, RegistroActividadDocumentoRequest } from '../types/interfaces';
import { useUserData } from '@/shared/hooks/useUserData.hook';
import type { ApiPaginatedResponse } from '@/shared/types/interfaces/Source';
import { TipoAccionRegistroActividad, type GrupoCanalArchivo } from '../types/enums';
import { TipoConsultaUsuarioEnum } from '../../escritorio/types/enums/TipoUsuario.enum';
import { GetFilesCasosByUsers, GetTotalsFilesByUsersOrEntity, obtenerUrlConSasToken, VizualizacionDescargaDocumento } from '../services/archivo.service';
import { useQuery, useMutation } from '@tanstack/react-query';
import { QUERY_KEYS } from '../utils';
import type { PerfilUsuarioSesion, PerfilEntidad } from '../../../shared/types/interfaces/user.interface';
import { storage } from '../../../shared/utils/Const.utils';
import { StorageKeys } from '../../../shared/types/enums/Storage.enum';
import { handleDownloadFile, obtenerTipoUsuario } from '../../../shared/utils/helpers';

interface UseArchivosReturn {
  documentos: DataFileTable[];
  loading: boolean;
  error: string | null;
  totalRows: number;
  totalPartes: number;
  totalTribunal: number;
  currentPage: number;
  pageSize: number;
  searchDocuments: (params?: { idGrupoCanal?: GrupoCanalArchivo; searchTerm?: string; pageNumber?: number; pageSize?: number } | undefined) => Promise<void>;
  refresh: () => Promise<void>;
  handlePageChange: (page: number) => Promise<void>;
  handlePerRowsChange: (newPerPage: number, page: number) => Promise<void>;
}

 export const useGetFilesCasoByUsers = (params: { 
    tipoConsulta?: TipoConsultaUsuarioEnum; 
    id?: number; 
    idGrupoCanal?: GrupoCanalArchivo; 
    searchTerm?: string;
    pageNumber?: number; 
    pageSize?: number }) => {
    return useQuery<ApiPaginatedResponse<DataFileTable, any>>({
        queryKey: [QUERY_KEYS.filesCasosByUser, params],
        queryFn: async () => await GetFilesCasosByUsers(params),
        refetchOnWindowFocus: false,
    });
  }
 export const useGetTotalsFilesByUsersOrEntity = (params: { 
    tipoConsulta: TipoConsultaUsuarioEnum; 
    id: number; 
    idGrupoCanal?: GrupoCanalArchivo;
  }) => {
    return useQuery<FileStats>({
        queryKey: [QUERY_KEYS.filesCasosByUser, params],
        queryFn: async () => await GetTotalsFilesByUsersOrEntity(params),
        refetchOnWindowFocus: false,
    });
  }
/**
 * Hook para obtener URL con SAS Token
 */
export const useObtenerUrlConSasToken = () => {
  return useMutation<
    { ok: boolean; message: string; urlProcessed: string },
    Error,
    string
  >({
    mutationFn: async (urlOrigen: string) => await obtenerUrlConSasToken(urlOrigen),
  });
};
/**
 * Hook personalizado para manejar la carga y búsqueda de archivos
 */
export const useArchivos = (): UseArchivosReturn => {
  const [queryParams, setQueryParams] = useState<{ idGrupoCanal?: GrupoCanalArchivo; searchTerm?: string; pageNumber?: number; pageSize?: number }>({ pageNumber: 1, pageSize: 10 });

  const { userData } = useUserData();
  const idUsuario = userData?.id ?? 0;
  const perfil: PerfilEntidad = storage.get(StorageKeys.PERFIL);
  const { id: perfilId, tipoConsulta: perfilTipoConsulta } = obtenerTipoUsuario(perfil, idUsuario);

  const { data: files, isLoading, error, refetch } = useGetFilesCasoByUsers({ id: perfilId, tipoConsulta: perfilTipoConsulta, ...queryParams });
  // const { data: totalFilesStats } = useGetTotalsFilesByUsersOrEntity({ id: perfilId, tipoConsulta: perfilTipoConsulta });

  /**
   * Busca documentos del usuario desde la API
   */
  const searchDocuments = async (params?: { 
      idGrupoCanal?: GrupoCanalArchivo; 
      searchTerm?: string;
      pageNumber?: number; 
      pageSize?: number }
      ): Promise<void> => {
    setQueryParams(prev => ({ ...prev, ...params }));
  };

  /**
   * Maneja el cambio de página
   */
  const handlePageChange = async (page: number): Promise<void> => {
    setQueryParams(prev => ({ ...prev, pageNumber: page }));
  };

  /**
   * Maneja el cambio de filas por página
   */
  const handlePerRowsChange = async (newPerPage: number, page: number): Promise<void> => {
    setQueryParams(prev => ({ ...prev, pageSize: newPerPage, pageNumber: page }));
  };

  /**
   * Función para refrescar los datos
   */
  const refresh = async (): Promise<void> => {
    await refetch();
  };

  return {
    documentos: files?.data || [],
    loading: isLoading,
    error: error?.message || null,
    totalRows: files?.rowCount || 0,
    totalPartes: files?.helper?.totalParte || 0,
    totalTribunal: files?.helper?.totalTribunal || 0,
    currentPage: files?.currentPage || 1,
    pageSize: files?.pageSize || 10,
    searchDocuments,
    refresh,
    handlePageChange,
    handlePerRowsChange,
  };
};

/* Registra las actividades de un documento segun la accion. */
export const useRegistroActividadDocumentoMutation = () => {
  return useMutation<any, any, RegistroActividadDocumentoRequest>({
    mutationFn: async ({ tipoAccion, idDocumento, idUsuario, tipoConsultaUsuario, idRelUsuarioEntidad }: RegistroActividadDocumentoRequest) => {
      console.log(idUsuario)
      console.log(tipoConsultaUsuario)
      console.log(idRelUsuarioEntidad)
      if(tipoConsultaUsuario === TipoConsultaUsuarioEnum.ABOGADO_PARALEGAL || tipoConsultaUsuario === TipoConsultaUsuarioEnum.ENTIDAD) return await VizualizacionDescargaDocumento(`?idTipoAccio=${tipoAccion}&IdDocumento=${idDocumento}&idusuario=${idUsuario}&idrelusuarioentidad=${idRelUsuarioEntidad}`);
      return await VizualizacionDescargaDocumento(`?idTipoAccio=${tipoAccion}&IdDocumento=${idDocumento}&idusuario=${idUsuario}`);
    },
    onError: (err) => {
      console.error(err)
    },
    onSuccess: (res) => {
      console.log(res)
    }
  })
}

/**
 * Hook desarrollado utilizando `Orchestrator Pattern` que orquesta la descarga de un archivo junto con el registro de la actividad del usuario.
 * Centraliza el flujo para asegurar que toda descarga quede auditada y registrada en las actividades de SGC correctamente.
 */
export const useActivityRegisterWhileDownloadFile = () => {
  const { mutate: mutateRegistrarActividadDocumento } = useRegistroActividadDocumentoMutation();

  const registerAndDownloadFile = async ( params: ActivityRegisterWhileDownloadFile ) => {
    const { idDocumento, idUsuario, urlDocumento, nombreDocumento, sasToken, tipoConsultaUsuario, idRelUsuarioEntidad } = params;
    try {
      console.log('useRegisterActivityFileDownload', params)
      handleDownloadFile({
        urlDocumento: urlDocumento, 
        sasToken: sasToken, 
        nombreDocumento: nombreDocumento
      })
      mutateRegistrarActividadDocumento({
        tipoAccion: TipoAccionRegistroActividad.DESCARGAR,
        idDocumento,
        idUsuario,
        tipoConsultaUsuario,
        idRelUsuarioEntidad
      });
    } catch (error) {
      console.error('Error al descargar el archivo use:', error);
    }
  }

  return {
    registerAndDownloadFile
  };
}